import React from 'react';
import { User, BookOpen, Calendar, Award, Mail } from 'lucide-react';

interface AuthorBioProps {
    variant?: 'full' | 'compact' | 'inline';
    showDate?: boolean;
    articleDate?: string;
    topic?: string;
}

/**
 * AuthorBio component displays editorial information to establish credibility
 * and E-E-A-T (Experience, Expertise, Authoritativeness, Trustworthiness) signals.
 * 
 * Use on pages with substantial content to show human editorial oversight.
 */
const AuthorBio: React.FC<AuthorBioProps> = ({ 
    variant = 'compact', 
    showDate = true, 
    articleDate,
    topic = 'astrology and symbolic systems'
}) => {
    const currentDate = articleDate || new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    });

    if (variant === 'inline') {
        return (
            <div className="flex items-center gap-3 text-sm text-gray-400 py-2">
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                    <User size={16} className="text-white" />
                </div>
                <div>
                    <span className="text-gray-300">Written by </span>
                    <span className="text-white font-medium">ZodiAge Editorial Team</span>
                    {showDate && (
                        <>
                            <span className="text-gray-500 mx-2">•</span>
                            <span>{currentDate}</span>
                        </>
                    )}
                </div>
            </div>
        );
    }

    if (variant === 'compact') {
        return (
            <div className="bg-cosmic-800/30 p-4 rounded-xl border border-cosmic-700/50">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center flex-shrink-0">
                        <User size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="font-bold text-white">ZodiAge Editorial Team</span>
                            <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">Verified</span>
                        </div>
                        <p className="text-gray-400 text-sm">
                            Our editorial team researches and creates content on {topic}, 
                            combining cultural knowledge with accessible explanations.
                        </p>
                        {showDate && (
                            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                                <Calendar size={12} />
                                <span>Last updated: {currentDate}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Full variant
    return (
        <article className="bg-cosmic-800/30 p-6 rounded-2xl border border-cosmic-700/50">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <User size={20} className="text-primary" />
                About the Author
            </h3>
            
            <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                        <User size={40} className="text-white" />
                    </div>
                </div>
                
                <div className="flex-1 space-y-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-xl font-bold text-white">ZodiAge Editorial Team</h4>
                            <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">Verified</span>
                        </div>
                        <p className="text-gray-400 text-sm">Content Creators & Researchers</p>
                    </div>
                    
                    <p className="text-gray-300 text-sm leading-relaxed">
                        The ZodiAge editorial team consists of writers, researchers, and developers with backgrounds 
                        in cultural studies, web development, and content creation. We research traditional sources 
                        on astrology, tarot, and symbolic systems while ensuring content is accessible to modern readers.
                    </p>
                    
                    <p className="text-gray-300 text-sm leading-relaxed">
                        Our approach combines historical accuracy with clear explanations, always emphasizing that 
                        astrological and mystical content is for entertainment and reflection purposes. We are not 
                        licensed practitioners and encourage users to consult professionals for important decisions.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-400">
                            <BookOpen size={14} className="text-primary" />
                            <span>Focus: {topic}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                            <Award size={14} className="text-primary" />
                            <span>Approach: Research-based, educational</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                            <Calendar size={14} className="text-primary" />
                            <span>Updated: {currentDate}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                            <Mail size={14} className="text-primary" />
                            <a href="mailto:info@zodiage.com" className="hover:text-primary transition-colors">
                                info@zodiage.com
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-cosmic-700/50">
                <h4 className="font-bold text-white mb-2 text-sm">Our Editorial Standards</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-400">
                    <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                        Research traditional and historical sources
                    </li>
                    <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                        Present content accessibly and clearly
                    </li>
                    <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                        Distinguish entertainment from advice
                    </li>
                    <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                        Update content for accuracy regularly
                    </li>
                </ul>
            </div>
        </article>
    );
};

/**
 * ContentDisclaimer component for displaying important disclaimers
 * about the nature of astrological content.
 */
export const ContentDisclaimer: React.FC<{ variant?: 'full' | 'compact' }> = ({ variant = 'compact' }) => {
    if (variant === 'compact') {
        return (
            <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-xl p-4 text-sm">
                <p className="text-yellow-200/80 flex items-start gap-2">
                    <span className="text-yellow-400 font-bold flex-shrink-0">⚠</span>
                    <span>
                        <strong className="text-yellow-300">Entertainment Only:</strong> All astrological, tarot, and zodiac 
                        content is provided for entertainment and educational purposes. These are symbolic systems, not 
                        scientifically validated prediction methods. Consult professionals for important decisions.
                    </span>
                </p>
            </div>
        );
    }

    return (
        <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-2xl p-6">
            <h3 className="text-lg font-bold text-yellow-300 mb-3 flex items-center gap-2">
                ⚠ Important Disclaimer
            </h3>
            <div className="space-y-3 text-sm text-yellow-200/80">
                <p>
                    All content on ZodiAge—including zodiac readings, tarot interpretations, personality profiles, 
                    and astrological forecasts—is provided <strong className="text-yellow-300">for entertainment 
                    and educational purposes only</strong>.
                </p>
                <p>
                    Astrology, tarot, and similar practices are symbolic and cultural systems with historical significance. 
                    They are not scientifically validated methods for predicting future events, diagnosing conditions, 
                    or making important life decisions.
                </p>
                <p>
                    <strong className="text-yellow-300">Do not rely on this content for:</strong> medical decisions, 
                    financial investments, legal matters, relationship decisions, or any situation requiring 
                    professional guidance. Always consult qualified professionals for such matters.
                </p>
            </div>
        </div>
    );
};

/**
 * LastUpdated component for showing content freshness
 */
export const LastUpdated: React.FC<{ date?: string }> = ({ date }) => {
    const displayDate = date || new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    });

    return (
        <div className="flex items-center gap-2 text-xs text-gray-500">
            <Calendar size={12} />
            <span>Last updated: {displayDate}</span>
        </div>
    );
};

/**
 * SourcesSection for citing sources and references
 */
export const SourcesSection: React.FC<{ sources?: string[] }> = ({ sources }) => {
    const defaultSources = [
        "Traditional astrological texts and teachings",
        "Academic research on cultural symbolism",
        "Historical documentation of zodiac and tarot systems",
        "Modern psychological interpretations of archetypes"
    ];

    const displaySources = sources || defaultSources;

    return (
        <div className="bg-cosmic-800/30 p-4 rounded-xl border border-cosmic-700/50 text-sm">
            <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                <BookOpen size={14} className="text-primary" />
                Sources & Research
            </h4>
            <p className="text-gray-400 mb-2">
                Our content is informed by:
            </p>
            <ul className="space-y-1 text-gray-400">
                {displaySources.map((source, index) => (
                    <li key={index} className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-primary rounded-full"></span>
                        {source}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default AuthorBio;
